import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Scissors, Play, Upload, X } from "lucide-react";
import { toast } from "sonner";

// Client-side trim + re-encode using MediaRecorder over video.captureStream().
// This drops the effective bitrate and cuts unwanted head/tail. Real-time only
// (recording takes as long as the trimmed segment plays back).
export default function VideoTrimmerDialog({ open, file, onCancel, onReady }) {
  const videoRef = useRef(null);
  const [url, setUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const onLoaded = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration || 0;
    setDuration(d);
    setStart(0);
    setEnd(Math.min(60, d));
  };

  const fmt = (t) => {
    const s = Math.round(t);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const doTrim = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (end - start < 1) { toast.error("النطاق قصير جداً"); return; }
    if (typeof v.captureStream !== "function" && typeof v.mozCaptureStream !== "function") {
      toast.error("متصفحك لا يدعم إعادة الترميز. جرّب Chrome/Firefox.");
      return;
    }
    setProcessing(true);
    setProgress(0);
    try {
      v.currentTime = start;
      await new Promise((r) => v.addEventListener("seeked", r, { once: true }));
      // Grab a poster frame from the start position
      let posterFile = null;
      try {
        const cvs = document.createElement("canvas");
        cvs.width = v.videoWidth || 640;
        cvs.height = v.videoHeight || 360;
        const ctx = cvs.getContext("2d");
        ctx.drawImage(v, 0, 0, cvs.width, cvs.height);
        const blob = await new Promise((res) => cvs.toBlob(res, "image/jpeg", 0.85));
        if (blob) posterFile = new File([blob], "poster.jpg", { type: "image/jpeg" });
      } catch { /* poster is best-effort */ }
      const stream = (v.captureStream ? v.captureStream() : v.mozCaptureStream()) ;
      const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
      let mimeType = "";
      for (const c of candidates) { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) { mimeType = c; break; } }
      const rec = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 900_000,
        audioBitsPerSecond: 96_000,
      });
      const chunks = [];
      rec.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);
      const done = new Promise((resolve) => { rec.onstop = () => resolve(); });
      v.muted = false;
      await v.play();
      rec.start(200);
      const tick = () => {
        if (!v || v.paused) return;
        setProgress(Math.min(100, ((v.currentTime - start) / (end - start)) * 100));
        if (v.currentTime >= end) {
          try { rec.stop(); } catch { /* noop */ }
          v.pause();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      await done;
      const outBlob = new Blob(chunks, { type: "video/webm" });
      const outFile = new File([outBlob], (file.name.replace(/\.[^.]+$/, "") + ".webm"), { type: "video/webm" });
      toast.success(`تمت المعالجة — ${(outFile.size / 1024 / 1024).toFixed(1)}MB${posterFile ? " + غلاف تلقائي" : ""}`);
      onReady(outFile, posterFile);
    } catch (e) {
      toast.error("فشل الترميز: " + (e?.message || "خطأ"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !processing) onCancel(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="video-trimmer">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black inline-flex items-center gap-2">
            <Scissors className="w-6 h-6 text-indigo-600" /> اقتصاص وضغط الفيديو
          </DialogTitle>
        </DialogHeader>

        {url && (
          <video
            ref={videoRef}
            src={url}
            controls
            onLoadedMetadata={onLoaded}
            className="w-full aspect-video bg-black rounded-xl"
            data-testid="trimmer-video"
          />
        )}

        {duration > 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold text-xs">بداية ({fmt(start)})</Label>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, duration - 1)}
                  step={0.5}
                  value={start}
                  onChange={(e) => {
                    const v = Math.min(parseFloat(e.target.value), end - 1);
                    setStart(v);
                    if (videoRef.current) videoRef.current.currentTime = v;
                  }}
                  className="w-full accent-indigo-600"
                  data-testid="trim-start"
                />
              </div>
              <div>
                <Label className="font-bold text-xs">نهاية ({fmt(end)})</Label>
                <input
                  type="range"
                  min={1}
                  max={duration}
                  step={0.5}
                  value={end}
                  onChange={(e) => {
                    const v = Math.max(parseFloat(e.target.value), start + 1);
                    setEnd(v);
                  }}
                  className="w-full accent-indigo-600"
                  data-testid="trim-end"
                />
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm text-indigo-800 flex items-center gap-2 flex-wrap">
              <Play className="w-4 h-4" />
              <span className="font-black">المدة المختارة:</span> {fmt(end - start)} من أصل {fmt(duration)}
              <span className="text-xs opacity-80 ms-auto">• جودة الإخراج: ~900kbps (WebM)</span>
            </div>
            {processing && (
              <div className="space-y-2">
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-gradient-to-l from-indigo-500 to-violet-600" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-xs text-slate-500 text-center">جاري إعادة الترميز... {Math.round(progress)}%</div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onCancel} disabled={processing} data-testid="trim-cancel">
                <X className="w-4 h-4" /> إلغاء
              </Button>
              <Button
                onClick={doTrim}
                disabled={processing}
                className="gap-2 bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
                data-testid="trim-confirm"
              >
                <Upload className="w-4 h-4" /> {processing ? "جارٍ..." : "اقتصاص ورفع"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
